import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MdArrowBack,
  MdFitnessCenter,
  MdChevronLeft,
  MdChevronRight,
  MdTune,
} from "react-icons/md";
import Card from "components/card";
import { ProgramService } from "../../../services/programService";
import { ExerciseService } from "../../../services/exerciseService";
import {
  TrainingDayDetail,
  UserExerciseActivityLog,
  TrainingProgram,
  Exercise,
} from "../../../types/program";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import { useTrainingDays } from "../../../hooks/useTrainingDays";
import { DndContext, DragOverlay, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { getYouTubeThumbnailFromUrl } from "../../../utils/youtube";
import SortableExerciseItem from "../../../components/programs/SortableExerciseItem";
import ExerciseToTrainingDayModal from "../../../components/programs/ExerciseToTrainingDayModal";
import TrainingMethodBadge from "../../../components/badges/TrainingMethodBadge";
import PersistentActionConfirmationModal from "../../../components/modals/PersistentActionConfirmationModal";
import MassAssignSetsModal from "../../../components/programs/MassAssignSetsModal";
import EmptyState from "components/common/EmptyState";
import LoadingSpinner from "components/common/LoadingSpinner";
import { logger } from "../../../utils/logger";

const TrainingDayDetailView: React.FC = () => {
  const { programId, weekId, dayId } = useParams<{
    programId: string;
    weekId: string;
    dayId: string;
  }>();
  const navigate = useNavigate();

  const [trainingDay, setTrainingDay] = useState<TrainingDayDetail | null>(
    null
  );
  const [program, setProgram] = useState<TrainingProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [programLoading, setProgramLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exercises, setExercises] = useState<any[]>([]);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [showMassAssignModal, setShowMassAssignModal] = useState(false);
  const [massAssignLoading, setMassAssignLoading] = useState(false);
  const [massAssignError, setMassAssignError] = useState<string | null>(null);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [pendingReorderedExercises, setPendingReorderedExercises] = useState<
    any[] | null
  >(null);
  const [originalExercises, setOriginalExercises] = useState<any[] | null>(
    null
  );

  const {
    totalDays,
    getPreviousDay,
    getNextDay,
    trainingWeeks,
    currentWeekIndex,
    setCurrentWeekIndex,
    getFilteredDaysForWeek,
  } = useTrainingDays({
    programId: programId ? parseInt(programId) : undefined,
    trainingDaysPerWeek: program?.training_days_per_week,
    currentDayId: dayId ? parseInt(dayId) : undefined,
  });

  // Drag and drop functionality
  const handleExerciseReorder = (reorderedExercises: any[]) => {
    setReorderError(null);
    setOriginalExercises([...exercises]);
    setExercises(reorderedExercises);
    setPendingReorderedExercises(reorderedExercises);
    setShowReorderModal(true);
  };

  const confirmReorder = async (isPersistent: boolean) => {
    if (!pendingReorderedExercises || !dayId) return;

    setReorderLoading(true);
    setReorderError(null);

    const positions: Record<number, number> = {};
    pendingReorderedExercises.forEach((exercise, index) => {
      if (exercise.activityLogId) {
        positions[exercise.activityLogId] = index + 1;
      }
    });

    try {
      const result = await ExerciseService.updateExercisePositions(
        parseInt(dayId),
        positions,
        isPersistent,
        true // isAdmin
      );

      if (!result.success) {
        setReorderError(
          result.error || "Failed to update exercise order. Please try again."
        );
        if (originalExercises) setExercises(originalExercises);
      }
      setShowReorderModal(false);
      setPendingReorderedExercises(null);
      setOriginalExercises(null);
    } catch (error: any) {
      setReorderError(
        error.message || "An error occurred while updating exercise order."
      );
      if (originalExercises) setExercises(originalExercises);
      setShowReorderModal(false);
      setPendingReorderedExercises(null);
      setOriginalExercises(null);
    } finally {
      setReorderLoading(false);
    }
  };

  const {
    sensors,
    handleDndDragStart,
    handleDndDragEnd,
    items,
    keyExtractor,
    activeItem,
  } = useDragAndDrop({
    items: exercises,
    onReorder: handleExerciseReorder,
    keyExtractor: (item) => item.activityLogId,
  });

  useEffect(() => {
    if (programId && weekId && dayId) {
      fetchTrainingDayDetail();
      fetchProgram();
    }
  }, [programId, weekId, dayId]);

  useEffect(() => {
    if (!weekId || trainingWeeks.length === 0) return;

    const idx = trainingWeeks.findIndex((week) => week.id === parseInt(weekId));

    if (idx !== -1 && idx !== currentWeekIndex) {
      setCurrentWeekIndex(idx);
    }
  }, [weekId, trainingWeeks, currentWeekIndex, setCurrentWeekIndex]);

  const fetchProgram = async () => {
    if (!programId) return;

    try {
      setProgramLoading(true);
      const programData = await ProgramService.getProgramById(
        parseInt(programId)
      );
      setProgram(programData);
    } catch (err: any) {
      logger.error("Error fetching program:", err);
    } finally {
      setProgramLoading(false);
    }
  };

  const fetchTrainingDayDetail = async () => {
    if (!programId || !weekId || !dayId) return;

    try {
      setLoading(true);
      setError(null);
      const detail = await ProgramService.getTrainingDayDetail(
        parseInt(programId),
        parseInt(weekId),
        parseInt(dayId)
      );
      setTrainingDay(detail);

      if (!detail) {
        setError("Training day not found.");
      } else {
        const exercisesFromLogs = getExercisesFromActivityLogs(
          detail.user_exercise_activity_logs
        );
        setExercises(exercisesFromLogs);
      }
    } catch (err: any) {
      const errorMessage =
        err.message || "Failed to load training day details.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/admin/programs/${programId}`);
  };

  const getExercisesFromActivityLogs = (logs: UserExerciseActivityLog[]) => {
    return logs
      .map((log) => ({
        ...log.exercise,
        position: log.position,
        activityLogId: log.id,
      }))
      .sort((a, b) => a.position - b.position);
  };

  const handleExerciseClick = (exerciseId: number) => {
    navigate(`/admin/exercise-library/${exerciseId}`);
  };

  const getActivityLogForExercise = (exerciseId: number) => {
    return (
      trainingDay?.user_exercise_activity_logs?.find(
        (log) => log.exercise.id === exerciseId
      ) || null
    );
  };

  const handleManageExercises = () => {
    setShowExerciseModal(true);
  };

  const handleMassAssign = () => {
    setMassAssignError(null);
    setShowMassAssignModal(true);
  };

  const handleMassAssignSubmit = async (data: {
    rpe?: number | null;
    rir?: number | null;
    rest_time_seconds?: number | null;
  }) => {
    if (!dayId) return;

    setMassAssignLoading(true);
    setMassAssignError(null);

    try {
      const result = await ProgramService.massAssignSetsData(
        parseInt(dayId),
        data
      );

      if (result.success) {
        setShowMassAssignModal(false);
        // Refresh the training day data to show updated values
        fetchTrainingDayDetail();
      } else {
        setMassAssignError(result.error || "Failed to mass assign values");
      }
    } catch (error: any) {
      setMassAssignError(error.message || "An unexpected error occurred");
    } finally {
      setMassAssignLoading(false);
    }
  };

  const handleModalUpdate = () => {
    fetchTrainingDayDetail();
  };

  const handlePreviousDay = () => {
    const prevDay = getPreviousDay();
    if (prevDay) {
      navigate(
        `/admin/programs/${programId}/weeks/${prevDay.weekId}/days/${prevDay.id}`
      );
    }
  };

  const handleNextDay = () => {
    const nextDay = getNextDay();
    if (nextDay) {
      navigate(
        `/admin/programs/${programId}/weeks/${nextDay.weekId}/days/${nextDay.id}`
      );
    }
  };

  const getWeekNavigationTarget = (offset: number) => {
    if (!trainingDay || trainingWeeks.length === 0) return null;

    const targetIndex = currentWeekIndex + offset;
    if (targetIndex < 0 || targetIndex >= trainingWeeks.length) return null;

    const targetWeek = trainingWeeks[targetIndex];
    const days = getFilteredDaysForWeek(targetWeek);

    if (!days || days.length === 0) return null;

    const matchingDay =
      days.find((day) => day.day_of_the_week === trainingDay.day_of_the_week) ||
      days[days.length - 1];

    if (!matchingDay) return null;

    return {
      weekId: targetWeek.id,
      dayId: matchingDay.id,
    };
  };

  const handleWeekNavigation = (offset: number) => {
    const target = getWeekNavigationTarget(offset);
    if (target && programId) {
      navigate(
        `/admin/programs/${programId}/weeks/${target.weekId}/days/${target.dayId}`
      );
    }
  };

  if (loading || programLoading) {
    return <LoadingSpinner />;
  }

  if (error || !trainingDay) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">
            {error || "Training day not found"}
          </div>
          <button
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Program</span>
          </button>
        </div>
      </Card>
    );
  }

  const previousWeekTarget = getWeekNavigationTarget(-1);
  const nextWeekTarget = getWeekNavigationTarget(1);

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Back to Program</span>
        </button>
      </div>

      {/* Training Day Header Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="flex items-center space-x-6">
          <div className="flex flex-col items-center space-y-2 rounded-lg bg-gray-50 px-4 py-2 dark:bg-navy-700">
            <div className="text-center">
              <div className="text-2xl font-bold text-biafit-primary">
                {trainingDay.training_week.sequence_number}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Week
              </div>
            </div>
            {trainingWeeks.length > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleWeekNavigation(-1)}
                  disabled={!previousWeekTarget}
                  className="rounded-lg border border-gray-300 p-1.5 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
                  title="Previous Week"
                >
                  <MdChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={() => handleWeekNavigation(1)}
                  disabled={!nextWeekTarget}
                  className="rounded-lg border border-gray-300 p-1.5 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
                  title="Next Week"
                >
                  <MdChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="mb-1 text-2xl font-bold text-navy-700 dark:text-white">
                  Day{" "}
                  {(trainingDay.training_week.sequence_number - 1) *
                    (program?.training_days_per_week || 7) +
                    trainingDay.day_of_the_week}
                </h1>
              </div>

              <div className="flex items-center space-x-3">
                {/* Day Navigation */}
                {totalDays > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePreviousDay}
                      disabled={!getPreviousDay()}
                      className="rounded-lg border border-gray-300 p-2 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
                      title="Previous Day"
                    >
                      <MdChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </button>

                    <button
                      onClick={handleNextDay}
                      disabled={!getNextDay()}
                      className="rounded-lg border border-gray-300 p-2 transition-colors hover:border-biafit-primary disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:border-biafit-primary"
                      title="Next Day"
                    >
                      <MdChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Exercises */}
      <Card extra="w-full p-3 md:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Exercises
            </h2>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {exercises.length} exercise{exercises.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Mass Assign Button - only show when there are exercises */}
            {exercises.length > 0 && (
              <button
                onClick={handleMassAssign}
                className="inline-flex items-center rounded-md bg-biafit-primary p-2 text-sm font-medium text-white shadow-sm hover:bg-biafit-secondary md:px-3 md:py-2"
                title="Mass Assign"
              >
                <MdTune className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Mass Assign</span>
              </button>
            )}
            <button
              onClick={handleManageExercises}
              className="inline-flex items-center rounded-md bg-biafit-primary p-2 text-sm font-medium text-white shadow-sm hover:bg-biafit-secondary md:px-3 md:py-2"
              title="Manage Exercises"
            >
              <MdFitnessCenter className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Manage Exercises</span>
            </button>
          </div>
        </div>

        {/* Reorder Error Message */}
        {reorderError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {reorderError}
                </p>
              </div>
            </div>
          </div>
        )}

        {exercises.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDndDragStart}
            onDragEnd={handleDndDragEnd}
          >
            <SortableContext
              items={items.map((item) => keyExtractor(item).toString())}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {exercises.map((exercise) => (
                  <SortableExerciseItem
                    key={exercise.activityLogId}
                    exercise={exercise}
                    activityLog={getActivityLogForExercise(exercise.id)}
                    onExerciseClick={handleExerciseClick}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeItem ? (
                <div className="rounded-lg border border-gray-200 bg-white p-4 opacity-95 shadow-lg dark:border-gray-700 dark:bg-navy-900">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-16 w-20">
                        {(activeItem as any).yt_link &&
                        getYouTubeThumbnailFromUrl(
                          (activeItem as any).yt_link
                        ) ? (
                          <img
                            src={getYouTubeThumbnailFromUrl(
                              (activeItem as any).yt_link
                            )}
                            alt={`${(activeItem as any).name} thumbnail`}
                            className="h-16 w-20 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-16 w-20 items-center justify-center rounded-lg bg-biafit-primary text-white">
                            <MdFitnessCenter className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-navy-700 dark:text-white">
                        {(activeItem as any).name ||
                          `Exercise ${activeItem.id}`}
                      </h3>
                      {/* Training Method Badge */}
                      <div className="mb-1">
                        <TrainingMethodBadge
                          trainingMethod={(activeItem as any).training_method}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <EmptyState
            icon={MdFitnessCenter}
            title="No Exercises Assigned"
            description="This training day doesn't have any exercises assigned yet."
            padding="sm"
          />
        )}

        {exercises.length > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={handleManageExercises}
              className="inline-flex items-center rounded-md bg-biafit-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-biafit-secondary"
            >
              <MdFitnessCenter className="mr-2 h-4 w-4" />
              Manage Exercises
            </button>
          </div>
        )}
      </Card>

      {/* Exercise Assignment Modal */}
      {trainingDay && (
        <ExerciseToTrainingDayModal
          isOpen={showExerciseModal}
          onClose={() => setShowExerciseModal(false)}
          trainingDayId={trainingDay.id}
          trainingDayName={`Day ${
            (trainingDay.training_week.sequence_number - 1) *
              (program?.training_days_per_week || 7) +
            trainingDay.day_of_the_week
          }`}
          currentExercises={exercises as Exercise[]}
          onUpdate={handleModalUpdate}
          userId={1} //default admin userId (on prod it is Holly)
          //userId={program?.created_by_id}
        />
      )}

      {/* Mass Assign Sets Modal */}
      {trainingDay && (
        <MassAssignSetsModal
          isOpen={showMassAssignModal}
          onClose={() => setShowMassAssignModal(false)}
          onSubmit={handleMassAssignSubmit}
          trainingDayName={`Day ${
            (trainingDay.training_week.sequence_number - 1) *
              (program?.training_days_per_week || 7) +
            trainingDay.day_of_the_week
          }`}
          exerciseCount={exercises.length}
          loading={massAssignLoading}
          error={massAssignError}
        />
      )}

      {/* Reorder Confirmation Modal */}
      <PersistentActionConfirmationModal
        isOpen={showReorderModal}
        onClose={() => {
          setShowReorderModal(false);
          if (originalExercises) setExercises(originalExercises);
          setPendingReorderedExercises(null);
          setOriginalExercises(null);
        }}
        onConfirm={confirmReorder}
        actionType="reorder"
        exerciseName="exercises"
        loading={reorderLoading}
      />
    </div>
  );
};

export default TrainingDayDetailView;
